/**
 * SQLite Schema Overview Service for Browser Mode
 *
 * Provides schema overview data (tables, views, relationships) for SQLite
 * databases running in the browser via sql.js.
 */

import type { Database } from 'sql.js';
import { getCurrentDatabase } from './sqliteManager';
import {
  getForeignKeyDependencies,
  getViewUsedTables,
} from './sqliteDependencies';

// ============================================================================
// Types (mirror electron/schema-overview/types.ts)
// ============================================================================

export type SchemaObjectType = 'table' | 'view' | 'procedure' | 'function' | 'trigger';

export type SchemaObject = {
  id: string;
  name: string;
  schema: string;
  type: SchemaObjectType;
  metadata?: {
    rowCount?: number;
    columnCount?: number;
    parameterCount?: number;
  };
};

export type SchemaRelationshipType = 'foreignKey' | 'viewReference' | 'routineReference';

export type SchemaRelationship = {
  sourceId: string;
  targetId: string;
  type: SchemaRelationshipType;
  label?: string;
};

export type SchemaStats = {
  tableCount: number;
  viewCount: number;
  procedureCount: number;
  functionCount: number;
  relationshipCount: number;
};

export type SchemaOverviewData = {
  objects: SchemaObject[];
  relationships: SchemaRelationship[];
  stats: SchemaStats;
};

export type GetSchemaOverviewResult = {
  success: boolean;
  data?: SchemaOverviewData;
  error?: string;
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Generate a unique ID for a schema object
 */
function generateObjectId(schema: string, name: string, type: SchemaObjectType): string {
  return `${schema || 'main'}.${name}.${type}`;
}

/**
 * Get column count for a table using PRAGMA table_info
 */
function getTableColumnCount(db: Database, tableName: string): number {
  try {
    const escapedName = tableName.replace(/'/g, "''");
    const result = db.exec(`PRAGMA table_info('${escapedName}')`);
    return result.length > 0 ? result[0].values.length : 0;
  } catch {
    return 0;
  }
}

/**
 * Get row count estimate for a table
 * Note: For large tables, this can be slow. In production, consider using estimates.
 */
function getTableRowCount(db: Database, tableName: string): number {
  try {
    const escapedName = tableName.replace(/"/g, '""');
    const result = db.exec(`SELECT COUNT(*) FROM "${escapedName}"`);
    if (result.length > 0 && result[0].values.length > 0) {
      return Number(result[0].values[0][0]) || 0;
    }
    return 0;
  } catch {
    return 0;
  }
}

// ============================================================================
// Main Service Functions
// ============================================================================

/**
 * Fetch all tables from the SQLite database
 */
function fetchTables(db: Database): SchemaObject[] {
  try {
    const result = db.exec(`
      SELECT name
      FROM sqlite_master
      WHERE type = 'table'
        AND name NOT LIKE 'sqlite_%'
      ORDER BY name
    `);

    if (result.length === 0) {
      return [];
    }

    return result[0].values.map((row) => {
      const name = row[0] as string;
      const columnCount = getTableColumnCount(db, name);
      const rowCount = getTableRowCount(db, name);

      return {
        id: generateObjectId('', name, 'table'),
        name,
        schema: '', // SQLite doesn't have schemas
        type: 'table' as SchemaObjectType,
        metadata: {
          columnCount,
          rowCount,
        },
      };
    });
  } catch (error) {
    console.error('[SQLite SchemaOverview] Error fetching tables:', error);
    return [];
  }
}

/**
 * Fetch all views from the SQLite database
 */
function fetchViews(db: Database): SchemaObject[] {
  try {
    const result = db.exec(`
      SELECT name
      FROM sqlite_master
      WHERE type = 'view'
      ORDER BY name
    `);

    if (result.length === 0) {
      return [];
    }

    return result[0].values.map((row) => {
      const name = row[0] as string;
      // Use PRAGMA table_info to get column count for views
      let columnCount = 0;
      try {
        const escapedName = name.replace(/'/g, "''");
        const colResult = db.exec(`PRAGMA table_info('${escapedName}')`);
        columnCount = colResult.length > 0 ? colResult[0].values.length : 0;
      } catch {
        // Ignore errors for column count
      }

      return {
        id: generateObjectId('', name, 'view'),
        name,
        schema: '',
        type: 'view' as SchemaObjectType,
        metadata: {
          columnCount,
        },
      };
    });
  } catch (error) {
    console.error('[SQLite SchemaOverview] Error fetching views:', error);
    return [];
  }
}

/**
 * Fetch foreign key relationships between tables
 */
function fetchForeignKeyRelationships(
  db: Database,
  tables: SchemaObject[]
): SchemaRelationship[] {
  const relationships: SchemaRelationship[] = [];

  for (const table of tables) {
    try {
      const fkDeps = getForeignKeyDependencies(db, table.name);

      for (const dep of fkDeps) {
        // Find the target table's ID
        const targetTable = tables.find(
          (t) => t.name.toLowerCase() === dep.name.toLowerCase()
        );

        if (targetTable) {
          // Get FK name if available
          const escapedName = table.name.replace(/'/g, "''");
          const fkResult = db.exec(`PRAGMA foreign_key_list('${escapedName}')`);
          let fkLabel: string | undefined;

          if (fkResult.length > 0) {
            // FK list returns: id, seq, table, from, to, on_update, on_delete, match
            const columns = fkResult[0].columns;
            const fromIdx = columns.indexOf('from');
            const toIdx = columns.indexOf('to');

            // Find the FK that references this target
            const fkRow = fkResult[0].values.find(
              (r) => (r[columns.indexOf('table')] as string).toLowerCase() === dep.name.toLowerCase()
            );

            if (fkRow) {
              const fromCol = fkRow[fromIdx] as string;
              const toCol = fkRow[toIdx] as string;
              fkLabel = `FK: ${fromCol} → ${toCol}`;
            }
          }

          relationships.push({
            sourceId: table.id,
            targetId: targetTable.id,
            type: 'foreignKey',
            label: fkLabel,
          });
        }
      }
    } catch (error) {
      console.error(`[SQLite SchemaOverview] Error fetching FKs for ${table.name}:`, error);
    }
  }

  return relationships;
}

/**
 * Fetch view-to-table relationships
 */
function fetchViewRelationships(
  db: Database,
  views: SchemaObject[],
  tables: SchemaObject[]
): SchemaRelationship[] {
  const relationships: SchemaRelationship[] = [];

  for (const view of views) {
    try {
      const usedTables = getViewUsedTables(db, view.name);

      for (const used of usedTables) {
        const targetTable = tables.find(
          (t) => t.name.toLowerCase() === used.name.toLowerCase()
        );

        if (targetTable) {
          relationships.push({
            sourceId: view.id,
            targetId: targetTable.id,
            type: 'viewReference',
          });
        }
      }
    } catch (error) {
      console.error(`[SQLite SchemaOverview] Error fetching view deps for ${view.name}:`, error);
    }
  }

  return relationships;
}

/**
 * Get schema overview data for the currently open SQLite database
 */
export function getSqliteSchemaOverview(): GetSchemaOverviewResult {
  const db = getCurrentDatabase();

  if (!db) {
    return {
      success: false,
      error: 'No SQLite database is currently open',
    };
  }

  try {
    // Fetch objects
    const tables = fetchTables(db);
    const views = fetchViews(db);

    // SQLite doesn't support stored procedures or functions
    const procedures: SchemaObject[] = [];
    const functions: SchemaObject[] = [];

    // Combine all objects
    const objects = [...tables, ...views];

    // Fetch relationships
    const fkRelationships = fetchForeignKeyRelationships(db, tables);
    const viewRelationships = fetchViewRelationships(db, views, tables);
    const relationships = [...fkRelationships, ...viewRelationships];

    // Calculate stats
    const stats: SchemaStats = {
      tableCount: tables.length,
      viewCount: views.length,
      procedureCount: procedures.length,
      functionCount: functions.length,
      relationshipCount: relationships.length,
    };

    return {
      success: true,
      data: {
        objects,
        relationships,
        stats,
      },
    };
  } catch (error) {
    console.error('[SQLite SchemaOverview] Error fetching schema overview:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch schema overview',
    };
  }
}
